import { AlertBanner, CustomButton, CustomCheckbox } from '@/shared/ui/components';
import { CloseIcon, saveBlueIcon } from '@/shared/ui/assets';
import { gettextReceivedItem } from '@/shared/lib';
import { DatePicker, Switch } from 'antd';
import dayjs from 'dayjs';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useReturnStatusCheckbox } from './useReturnStatusCheckbox';
import type { ReturnStatusCheckboxProps } from './returnStatusCheckbox.types';
import './style.scss';

/**
 * Renders the device return status form for a study: a 'Device returned' switch, and once it is on the received items checkbox list plus a return date and time picker limited to dates no later than today. Unsaved edits reveal reset and save buttons, every control is disabled while a blocking condition or an in-flight save exists, and that blocking reason is repeated below as a warning banner.
 *
 * @param data - Study return data used to seed the switch, received items and return date.
 * @param updateStudyDetail - Callback propagating the saved return status to the study details.
 * @returns The return status form with its optional warning banner.
 */
const ReturnStatusCheckbox = ({ data, updateStudyDetail }: ReturnStatusCheckboxProps) => {
  const {
    state,
    isChangeData,
    disabledMessage,
    onChangeCheckBox,
    onSubmit,
    onChangeSwitch,
    onChangeDate,
    resetData,
  } = useReturnStatusCheckbox({ data, updateStudyDetail });

  /**
   * Renders one CustomCheckbox per entry of the received items state for the device return form. Each checkbox reflects the item's checkBox flag, is labelled through the localized received-item helper, reports changes by item name and is disabled while a disabled message is present or a save is in flight.
   *
   * @returns The list of received-item checkbox elements.
   */
  const renderListCheckBox = () =>
    state.receivedItems.map((value: any, index: any) => (
      <CustomCheckbox
        checked={value.checkBox}
        className="custom-checkbox"
        id={`return-checkbox-${index}`}
        key={value.name}
        onChange={(checked) => {
          onChangeCheckBox(value.name, checked);
        }}
        disabled={!!disabledMessage || state.isSaving}
        label={gettextReceivedItem(value.name)}
      />
    ));

  return (
    <div className="return-status-checkbox-container">
      <div className="return-status-checkbox">
        {isChangeData && (
          <div className="selection__modifier --status-checkbox">
            <CustomButton
              id="close-btn"
              icon={<ReactSVG src={CloseIcon} />}
              size="small"
              style="neutral"
              type="outline"
              disabled={state.isSaving}
              onClick={resetData}
              ariaLabel={t`Cancel`}
            />
            <CustomButton
              id="save-btn"
              icon={<ReactSVG src={saveBlueIcon} />}
              size="small"
              style="default"
              type="secondary"
              loading={state.isSaving}
              onClick={onSubmit}
              className="save-return-btn"
            >
              {t`Save`}
            </CustomButton>
          </div>
        )}
        <div className="return-status-checkbox__toggle">
          <Switch
            {...({
              width: 44,
              height: 24,
              uncheckedIcon: false,
              checkedIcon: false,
              checked: state.markDeviceReturned,
              disabled: Boolean(disabledMessage) || state.isSaving,
              onChange: onChangeSwitch,
              id: 'device-returned-switch',
              'aria-label': t`Device returned`,
            } as any)}
          />
          <div className="label">{t`Device returned`}</div>
        </div>
        {state.markDeviceReturned && (
          <>
            <div className="return-status-checkbox__body">
              <div className="title">{t`Received items`}</div>
              <div className="list">{renderListCheckBox()}</div>
            </div>
            <div className="return-date__body">
              <div className="title">{t`Return date & time`}</div>
              <div className="return-date">
                <DatePicker
                  value={state.returnDate}
                  className="date-time"
                  onChange={onChangeDate}
                  disabledDate={(currentDate) => currentDate.startOf('day') > dayjs()}
                  showTime={{ format: 'HH:mm' }}
                  format="MM/DD/YYYY HH:mm"
                  needConfirm
                  disabled={Boolean(disabledMessage) || state.isSaving}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {!!disabledMessage && (
        <AlertBanner
          description={disabledMessage}
          alertType="warning"
          className="disabled-message"
        />
      )}
    </div>
  );
};

export default ReturnStatusCheckbox;
